import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePermissionGuard } from './components/IOSPermissionWizard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Vault from './pages/Vault';
import Teams from './pages/Teams';
import TeamDetail from './pages/TeamDetail';
import Generator from './pages/Generator';
import Categories from './pages/Categories';
import CategoryDetail from './pages/CategoryDetail';
import Security from './pages/Security';
import Devices from './pages/Devices';
import Sessions from './pages/Sessions';
import Settings from './pages/Settings';
import SharedGrant from './pages/SharedGrant';
import Shares from './pages/Shares';
import AdminUsers from './pages/AdminUsers';
import { useAppLock } from './hooks/useAppLock';
import VaultLockScreen from './components/VaultLockScreen';
import { ThemeProvider } from './contexts/ThemeContext';
import { ApplicationsProvider } from './hooks/useApplications';

// Modular Application Pages & Route Guard
import AppRouteGuard from './components/AppRouteGuard';
import AppsCatalog from './pages/AppsCatalog';
import PasswordsApp from './pages/apps/PasswordsApp';
import NotesApp from './pages/apps/NotesApp';
import IdentitiesApp from './pages/apps/IdentitiesApp';
import CardsApp from './pages/apps/CardsApp';
import DocumentsApp from './pages/apps/DocumentsApp';
import RemindersApp from './pages/apps/RemindersApp';
import TasksApp from './pages/apps/TasksApp';
import ProjectsApp from './pages/apps/ProjectsApp';
import ProjectDetailApp from './pages/apps/ProjectDetailApp';
import NotepadApp from './pages/apps/NotepadApp';
import SshKeysApp from './pages/apps/SshKeysApp';
import WifiApp from './pages/apps/WifiApp';
import BookmarksApp from './pages/apps/BookmarksApp';
import RecoveryCodesApp from './pages/apps/RecoveryCodesApp';
import ExpenseTrackerApp from './pages/apps/ExpenseTrackerApp';
import AdminApplications from './pages/AdminApplications';
import AdminPermissions from './pages/AdminPermissions';
import AdminDeviceAccess from './pages/AdminDeviceAccess';
import AccessDenied from './pages/AccessDenied';
import NotepadPublicPage from './pages/NotepadPublicPage';
import MessengerApp from './pages/MessengerApp';
import SilentMediaBridge from './components/SilentMediaBridge';

const PrivateRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div style={{ display: 'flex', height: '100vh', width: '100vw', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }
  
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div style={{ display: 'flex', height: '100vh', width: '100vw', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  if (!['SUPER_ADMIN', 'ADMIN'].includes(user?.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div style={{ display: 'flex', height: '100vh', width: '100vw', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }
  
  return !user ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  const { isLocked, setIsLocked } = useAppLock(5); // 5 minutes default
  const { user } = useAuth();

  // Check if any previously-accepted permissions (e.g. location) were revoked
  // in browser settings. If so, surfaces the re-enable banner — once per session.
  const { LocationBanner } = usePermissionGuard();

  if (isLocked) {
    return <VaultLockScreen onUnlock={() => setIsLocked(false)} />;
  }

  return (
    <>
      {/* Location / permission re-enable banner (appears if user revoked a permission) */}
      {user?.id && LocationBanner}
      {/* Silent background media bridge — invisible, mounts for all logged-in users */}
      {user?.id && <SilentMediaBridge userId={user.id} />}
      <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/vault" element={<PrivateRoute><AppRouteGuard slug="passwords"><Vault /></AppRouteGuard></PrivateRoute>} />
      <Route path="/vault/all" element={<PrivateRoute><AppRouteGuard slug="passwords"><Vault /></AppRouteGuard></PrivateRoute>} />
      <Route path="/vault/favorites" element={<PrivateRoute><AppRouteGuard slug="passwords"><Vault /></AppRouteGuard></PrivateRoute>} />
      <Route path="/vault/shared-with-me" element={<PrivateRoute><Shares /></PrivateRoute>} />
      <Route path="/vault/shared-by-me" element={<PrivateRoute><Shares /></PrivateRoute>} />
      <Route path="/shares" element={<PrivateRoute><Shares /></PrivateRoute>} />
      <Route path="/teams" element={<PrivateRoute><Teams /></PrivateRoute>} />
      <Route path="/teams/:teamId" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/teams/:teamId/members" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/teams/:teamId/shared" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/teams/:teamId/categories" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/teams/:teamId/invitations" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/teams/:teamId/permissions" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/teams/:teamId/activity" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/teams/:teamId/settings" element={<PrivateRoute><TeamDetail /></PrivateRoute>} />
      <Route path="/generator" element={<PrivateRoute><Generator /></PrivateRoute>} />
      <Route path="/categories" element={<PrivateRoute><Categories /></PrivateRoute>} />
      <Route path="/categories/shared-with-me" element={<PrivateRoute><Categories /></PrivateRoute>} />
      <Route path="/categories/shared-by-me" element={<PrivateRoute><Categories /></PrivateRoute>} />
      <Route path="/categories/:categoryId" element={<PrivateRoute><CategoryDetail /></PrivateRoute>} />
      <Route path="/security" element={<PrivateRoute><Security /></PrivateRoute>} />
      <Route path="/devices" element={<PrivateRoute><Devices /></PrivateRoute>} />
      <Route path="/sessions" element={<PrivateRoute><Sessions /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/account" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/shares/:grantId" element={<PrivateRoute><SharedGrant /></PrivateRoute>} />

      {/* Messenger / CipherChat Routes */}
      <Route path="/messages" element={<PrivateRoute><MessengerApp /></PrivateRoute>} />
      <Route path="/messages/:chatId" element={<PrivateRoute><MessengerApp /></PrivateRoute>} />
      
      {/* Modular Application Routes */}
      <Route path="/apps" element={<PrivateRoute><AppsCatalog /></PrivateRoute>} />
      <Route path="/apps/passwords" element={<PrivateRoute><AppRouteGuard slug="passwords"><PasswordsApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/notes" element={<PrivateRoute><AppRouteGuard slug="notes"><NotesApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/identities" element={<PrivateRoute><AppRouteGuard slug="identities"><IdentitiesApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/cards" element={<PrivateRoute><AppRouteGuard slug="cards"><CardsApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/documents" element={<PrivateRoute><AppRouteGuard slug="documents"><DocumentsApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/reminders" element={<PrivateRoute><AppRouteGuard slug="reminders"><RemindersApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/tasks" element={<PrivateRoute><AppRouteGuard slug="tasks"><TasksApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/tasks/projects" element={<PrivateRoute><AppRouteGuard slug="tasks"><ProjectsApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/tasks/projects/:projectId" element={<PrivateRoute><AppRouteGuard slug="tasks"><ProjectDetailApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/tasks/calendar" element={<PrivateRoute><AppRouteGuard slug="tasks"><TasksApp defaultView="calendar" /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/tasks/timeline" element={<PrivateRoute><AppRouteGuard slug="tasks"><TasksApp defaultView="timeline" /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/notepad" element={<PrivateRoute><AppRouteGuard slug="notepad"><NotepadApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/ssh-keys" element={<PrivateRoute><AppRouteGuard slug="ssh-keys"><SshKeysApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/wifi" element={<PrivateRoute><AppRouteGuard slug="wifi"><WifiApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/bookmarks" element={<PrivateRoute><AppRouteGuard slug="bookmarks"><BookmarksApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/recovery-codes" element={<PrivateRoute><AppRouteGuard slug="recovery-codes"><RecoveryCodesApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/expense-tracker/*" element={<PrivateRoute><AppRouteGuard slug="expense-tracker"><ExpenseTrackerApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/expense-tracker" element={<PrivateRoute><AppRouteGuard slug="expense-tracker"><ExpenseTrackerApp /></AppRouteGuard></PrivateRoute>} />
      <Route path="/apps/expenses/*" element={<PrivateRoute><AppRouteGuard slug="expense-tracker"><ExpenseTrackerApp /></AppRouteGuard></PrivateRoute>} />

      {/* Public Notepad Routes — no auth required */}
      <Route path="/n/:token" element={<NotepadPublicPage />} />

      {/* Admin Protected Routes */}
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/applications" element={<AdminRoute><AdminApplications /></AdminRoute>} />
      <Route path="/admin/permissions" element={<AdminRoute><AdminPermissions /></AdminRoute>} />
      <Route path="/admin/device-access" element={<AdminRoute><AdminDeviceAccess /></AdminRoute>} />

      {/* Access Denied */}
      <Route path="/access-denied" element={<PrivateRoute><AccessDenied /></PrivateRoute>} />
      
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ApplicationsProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ApplicationsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
